import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { AlignLeft } from 'lucide-react'
import { Textarea } from '@/src/renderer/components/ui/Textarea'
import { SubtaskList } from '../../SubtaskList'
import { useNestActions } from '../../../hooks/useNestActions'
import type { SubtaskEditor } from '../../../hooks/useSubtaskEditor'
import { LAYOUT_SHIFT } from '@/src/renderer/lib/motion/heightMotion'
import { resolveExpandedLayout } from '../../../utils/resolveExpandedLayout'
import type { DetailsSection, DetailsSectionId, DetailsChipId } from '../useDetailsSections'
import type { DetailsValues } from '../detailsValues'
import type { TaskDetailsUpdate } from '../TaskDetails.types'
import { DetailsChipRow } from './DetailsChipRow'
import './DetailsHeader.scss'

export interface DetailsHeaderProps {
  /** Die Einträge mit `place: 'head'` — bei mehreren Aufgaben ohne Titel, Notiz, Unteraufgaben. */
  sections: DetailsSection[]
  anchorId: string
  values: DetailsValues
  onUpdate: TaskDetailsUpdate
  /** Entwurf der Notiz — gedrosselt geschrieben, siehe `TaskDetails`. */
  notesDraft: string
  onNotesDraftChange: (value: string) => void
  /** Enter verlässt das Feld, Shift+Enter bricht die Zeile um — siehe `TaskDetails`. */
  onNotesKeyDown?: (e: React.KeyboardEvent) => void
  /**
   * ↑ und ↓ gehen ein Feld weiter (`fieldChain`). Am Kasten und nicht an der
   * Zeile: `SubtaskList` gehört auch der aufgeklappten Karte, wo diese Kette nicht
   * gilt — und Tastendrücke steigen in React ohnehin auf.
   */
  onSubtasksKeyDown?: (e: React.KeyboardEvent) => void
  /** Pfeil an einer Unteraufgabe: die Details zeigen sie (T14 #179). */
  onOpenSubtask?: (id: string) => void
  /** Marke der Kommentare an einer Unteraufgabe: die Details zeigen sie, auf „Kommentare" (#183). */
  onOpenSubtaskComments?: (id: string) => void
  onOpenSubtaskReminders?: (id: string, tab?: 'at') => void
  /** Die Checkliste des Ankers; sie schreibt schon selbst nach Dexie. */
  subtasks: SubtaskEditor
  openChip: DetailsChipId | null
  /** Der Chip „Erinnerung“ geht beim Anlegen auf „Datum & Uhrzeit“ auf (der Menüeintrag). */
  reminderTab?: 'at' | null
  onOpenChipChange: (id: DetailsChipId | null) => void
  /**
   * Wo der Caret im Kopf gerade steckt — `null`, wenn er ihn verlassen
   * hat. Die OBERE Pille der Zeichen-Leiste tönt danach; die untere folgt dem
   * Rollstand (entschieden 2026-09-19).
   */
  onActiveChange: (id: DetailsSectionId | null) => void
}

/**
 * **Der Kopf der Aufgaben-Details** (entschieden 2026-09-19) — Notiz,
 * Unteraufgaben und darunter die drei Chips.
 *
 * **Der Titel steht NICHT hier**, sondern oben in der Kopfzeile der Karte neben
 * den Pfeilen (`TaskDetails`), so wie im Entwurf. Er stand kurzzeitig an beiden
 * Orten — derselbe Satz zweimal.
 *
 * Optik und Geste wie in der aufgeklappten Karte: Felder ohne Rahmen im
 * Ruhezustand, die Notiz wächst mit. **Nicht** deren Schreibmodell — die Karte
 * hält Titel und Notiz bis zum Abschicken in ihrem eigenen Zustand
 * (`useTaskEntryForm`), die Aufgaben-Details schreiben gedrosselt sofort. Zwei
 * offene Editoren mit zwei Entwürfen desselben Satzes sind genau das, wogegen
 * CONTEXT.md (Aufgaben-Details) entschieden hat.
 *
 * @example
 * <DetailsHeader sections={headSections} anchorId={anchor.id} values={values}
 *   onUpdate={onUpdate} … onActiveChange={setHeadActive} />
 */
export const DetailsHeader: React.FC<DetailsHeaderProps> = ({
  sections, anchorId, values, onUpdate,
  notesDraft, onNotesDraftChange, onNotesKeyDown, onSubtasksKeyDown, onOpenSubtask, onOpenSubtaskComments, onOpenSubtaskReminders, subtasks,
  openChip, onOpenChipChange, reminderTab, onActiveChange,
}) => {
  const { t } = useTranslation()
  const { nest, unnest } = useNestActions()
  const unnestSubtask = useCallback((ids: string[], where: 'above' | 'below') => { void unnest(ids, where) }, [unnest])
  // Die Details stehen neben der Liste: Umgehängte Zeilen verlassen die Checkliste still (wie der Knopf im Kopf).
  const moveSubtasks = useCallback((ids: string[], parentId: string) => { void nest(ids, parentId, { select: false }) }, [nest])
  const has = useCallback(
    (id: DetailsSectionId) => sections.some(section => section.id === id),
    [sections],
  )

  /*
    **Dieselbe Regel wie in der aufgeklappten Karte** — sie steht in
    `resolveExpandedLayout` und gilt für beide Orte, sonst liefe sie auseinander. Hier
    zählt nur `notes`: Die Belohnung ist im Kopf ein Chip, und die Unteraufgaben brechen
    per Layout um statt den Platz zu wechseln (siehe unten).
  */
  const layout = resolveExpandedLayout({
    hasNotes: notesDraft.trim().length > 0,
    hasSubtasks: subtasks.items.length > 0,
    hasReward: false,
  })

  /*
    **Was umbricht, wandert — es springt nicht.** Die Unteraufgabe wechselt beim ersten
    Zeichen in der Notiz von „neben ihr" nach „unter ihr"; Framers `layout` fährt sie
    dorthin. `position` und nicht `layout`: Ihre Größe ändert sich dabei nicht, und eine
    mitanimierte Breite verzöge die Zeile beim Tippen.

    Die NOTIZ bekommt das bewusst nicht. Sie ist das, was unter den Fingern liegt — ihre
    Breite muss dem Anschlag sofort folgen, nicht ihm hinterherlaufen.
  */
  const reduced = useReducedMotion()

  /*
    **Beim Aufbau wird nicht gewandert** (Nutzer-Vorgabe, 2026-09-19).

    Die Checkliste kommt aus der Datenquelle, also erst einen Augenblick nach dem
    ersten Bild. Bis dahin sieht sie aus wie eine leere Liste und steht neben der
    Notiz; mit ihrer Ankunft nimmt sie die Zeile, und die Chip-Zeile rutschte mit
    einer Bewegung nach unten — beim bloßen ÖFFNEN, ohne dass jemand etwas getan
    hätte. Bewegt wird erst, was sich danach ändert.

    Das Tor geht ein Bild NACH der Ankunft auf: Fiele es im selben Bild, wäre die
    Ankunft selbst die erste Bewegung.
  */
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    if (!subtasks.loaded) return
    const frame = requestAnimationFrame(() => setSettled(true))
    return () => cancelAnimationFrame(frame)
  }, [subtasks.loaded])

  // `false` statt einer Dauer von 0: Das Prop selbst muss weg. Framer baut sonst seine
  // Projection auf, auch wenn nichts zu sehen ist — und wo nicht gemessen werden kann
  // (jsdom, ein Tab hinter `display: none`), bleibt sie unfertig und hält den Abgang
  // eines Geschwisters auf (nachgemessen 19.09.2026).
  const shift = reduced || !settled ? false : ('position' as const)

  // `onFocus`/`onBlur` steigen in React auf (sie hängen an focusin/focusout) —
  // ein Griff am Kasten reicht für alles darin, auch für die Zeilen der
  // Checkliste, die selbst keine Fokus-Griffe anbieten.
  const focusReporter = (id: DetailsSectionId) => ({
    onFocus: () => onActiveChange(id),
    onBlur: () => onActiveChange(null),
  })

  return (
    <div className="details-header">
      {/*
        **Notiz und Unteraufgabe teilen sich eine Zeile** (Figma `5362:160710`) — nach
        derselben Regel wie die aufgeklappte Karte (`resolveExpandedLayout`): Solange die
        Notiz leer ist, stehen beide nebeneinander; sobald sie Text trägt, nimmt sie die
        Zeile für sich und die Unteraufgabe rutscht darunter.

        **Umgebrochen wird per `flex-wrap`, nicht durch Umhängen.** Die Karte hängt ihren
        Unteraufgaben-Knoten zwischen zwei Eltern um; hier wäre das teuer: Die Liste baute
        bei jedem Wechsel neu auf, und ein halb getippter Entwurf wäre beim ersten Zeichen
        in der Notiz weg. Als Geschwister mit `flex-basis: 100%` an der gefüllten Notiz
        kommt derselbe Umbruch ohne Neuaufbau zustande.
      */}
      <div className="details-header__row">
        {has('notes') && (
          // Wächst mit dem Text (`Textarea` misst selbst) — deshalb kein `rows`.
          <div className={`details-header__notes details-header__notes--${layout.notes}`}>
            {/* Dasselbe Zeichen wie in der aufgeklappten Karte. */}
            <AlignLeft size={16} className="details-header__notes-icon" aria-hidden="true" />
            <Textarea
              aria-label={t('taskDetails.section.notes')}
              placeholder={t('taskDetails.header.notesPlaceholder')}
              value={notesDraft}
              onChange={e => onNotesDraftChange(e.target.value)}
              onKeyDown={onNotesKeyDown}
            />
          </div>
        )}

        {has('subtasks') && (
          // `data-section` misst der Scroll-Spy: Die Unteraufgaben sind die erste
          // Station der Leiste, die man durch Rollen erreicht.
          <motion.div
            layout={shift}
            transition={LAYOUT_SHIFT}
            className={`details-header__subtasks details-header__subtasks--${layout.subtasks === 'list' ? 'list' : 'placeholder'}`}
            data-section="subtasks"
            onKeyDown={onSubtasksKeyDown}
            {...focusReporter('subtasks')}
          >
            {/*
              Erst, wenn die Daten da sind: Die Zeilen fahren weich auf, sobald
              sie neu in die Liste kommen — stünde die Liste vorher leer da,
              führen beim bloßen Öffnen alle auf, und die Chip-Zeile darunter
              wanderte mit (gemessen 2026-09-23: 20 Lagen statt 2).
            */}
            {subtasks.loaded && (
              <SubtaskList
                items={subtasks.items}
                onAdd={subtasks.add}
                onInsertAfter={subtasks.insertAfter}
                onMove={subtasks.move}
                onToggle={subtasks.toggle}
                onRename={subtasks.rename}
                onRemove={subtasks.remove}
                onRestore={subtasks.restore}
                onOpen={onOpenSubtask}
                onOpenComments={onOpenSubtaskComments}
                onOpenReminders={onOpenSubtaskReminders}
                onUnnest={unnestSubtask}
                onUpdate={subtasks.update}
                parentId={anchorId}
                onMoveTo={moveSubtasks}
                parentRecurring={values.recurrence != null}
                onDuplicate={subtasks.duplicate}
                onIndent={subtasks.indent}
                onOutdent={subtasks.outdent}
                coupledSpark
              />
            )}
          </motion.div>
        )}
      </div>

      {/* Die Chips rücken mit — sonst wanderte die Unteraufgabe weich und die Reihe
          darunter spränge im selben Moment. */}
      <motion.div layout={shift} transition={LAYOUT_SHIFT}>
      <DetailsChipRow
        // Die Chip-Zeile ist die letzte Station VOR den Bereichen. Sie trägt
        // die Kennung des ERSTEN ihrer drei Zeichen: Aufmerksamkeit, Tags und
        // Belohnung stehen nebeneinander und haben dieselbe Oberkante — der
        // Rollstand kann sie nicht trennen. Die beiden anderen leuchten,
        // solange ihr Kasten offen steht.
        data-section="attention"
        anchorId={anchorId}
        values={values}
        onUpdate={onUpdate}
        openChip={openChip}
        onOpenChipChange={onOpenChipChange}
        withoutReward={!has('reward')}
        reminderTaskId={has('reminders') ? anchorId : undefined}
        reminderTab={reminderTab}
      />
      </motion.div>
    </div>
  )
}
